/**
 * cart-vial-upsell custom element (Maison Noir)
 * --------------------------------------------------------------------------
 * Renders inside <cart-drawer>. Reads its own data-* attributes to know which
 * line-item property to attach (`_free_vial: true` or `_upsell: true`) so the
 * matching Shopify automatic discount can target the line at checkout.
 */

class CartVialUpsell extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.cart-vial-upsell__add');
    this.select = this.querySelector('.cart-vial-upsell__select');

    if (!this.button || !this.select) {
      return;
    }

    this.handleClick = this.handleClick.bind(this);
    this.button.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.handleClick);
    }
  }

  async handleClick(event) {
    event.preventDefault();

    const variantId = this.select.value;
    if (!variantId) {
      return;
    }

    const propertyName = this.dataset.propertyName;
    const propertyValue = this.dataset.propertyValue;

    this.button.setAttribute('aria-busy', 'true');
    this.button.disabled = true;

    document.documentElement.dispatchEvent(
      new CustomEvent('theme:loading:start', { bubbles: true })
    );

    const sectionsToBundle = new Set();
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    if (propertyName && propertyValue) {
      formData.append(`properties[${propertyName}]`, propertyValue);
    }

    if (sectionsToBundle.size > 0) {
      formData.append('sections', [...sectionsToBundle].join(','));
    }

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      const responseJson = await response.json();

      if (!response.ok) {
        document.dispatchEvent(
          new CustomEvent('toast:show', {
            detail: { message: responseJson.message || 'Could not add the vial.', tone: 'error' },
          })
        );
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true })
        );
        return;
      }

      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent.sections = responseJson.sections;

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'cart-vial-upsell:add', cart: cartContent },
        })
      );
    } catch (error) {
      document.dispatchEvent(
        new CustomEvent('toast:show', {
          detail: { message: 'Could not add the vial. Please try again.', tone: 'error' },
        })
      );
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true })
      );
    } finally {
      document.documentElement.dispatchEvent(
        new CustomEvent('theme:loading:end', { bubbles: true })
      );
      this.button.removeAttribute('aria-busy');
      this.button.disabled = false;
    }
  }
}

if (!window.customElements.get('cart-vial-upsell')) {
  window.customElements.define('cart-vial-upsell', CartVialUpsell);
}
